// US-087: pure row-shape builder for the Create-PO fast product list (Phase 1).
//
// Turns one row from GET /supplier-products/supplier/:id (SupplierModel.fetchProductsOptimized,
// see ISupplierProductDisplay in src/models/supplier.ts) into the same row shape the
// existing table template expects from the slower /questions/report call — so the
// template's cell bindings (variant_image, Item[Barcode], etc.) work unmodified
// regardless of which phase populated the row.
//
// Reorder-parameter fields (min/max order qty, lead time, order cycle, ...) already
// share identical bracketed keys between both endpoints (see REORDER_PARAM_COLUMNS in
// reorderParamColumns.ts), so they pass through via the spread untouched. Only `image`
// and `product_barcode` need renaming; `case_qty` has no Phase-1 equivalent at all and
// is left undefined until Phase 2 merges it in.
//
// Import-free of Vue so it unit-tests without mounting the component.

import { resolveUnitCost } from '@/helpers/poCostResolver';

export interface BuildFastProductRowOptions {
  /** SupplierProduct.cost for this variant, from the already-loaded supplierCostMap. */
  supplierCost: number | null;
  exchangeRate: number | null;
  isForeignCurrency: boolean;
  supplierCurrencyCode: string;
}

/**
 * Build a Create-PO table row from one Phase-1 (fast) supplier-product record.
 * `quantity` starts at 0 — Phase 1 carries no forecast, so the buyer fills it in
 * manually until Phase 2's suggestion (if any) lands. `suggestionState` starts
 * 'pending' so the table can show a loading placeholder for report-only columns.
 */
export function buildFastProductRow(p: any, opts: BuildFastProductRowOptions): any {
  // Phase 1 has no Shopify average-cost figure (that's report-only), so the ladder's
  // step-2/3 fallback has nothing to convert yet — shopifyCost is 0 until Phase 2 merges
  // in the real figure. Step 1 (supplier cost) still resolves correctly immediately.
  const { cost: effectiveCost, source: costSource } = resolveUnitCost(
    opts.supplierCost, 0, opts.exchangeRate, opts.isForeignCurrency, opts.supplierCurrencyCode,
  );
  return {
    ...p,
    'variant_id': Number(p.variant_id),
    'variant_image': p.image,
    'Item[Barcode]': p.product_barcode,
    'quantity': 0,
    'original_average_unit_cost': effectiveCost,
    '[average_unit_cost]': effectiveCost,
    'total': 0,
    'cost_source': costSource,
    'userEdited': false,
    'suggestionState': 'pending',
  };
}
